import React, { useState, useEffect, useRef } from "react";
import {
  Alert,
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  Avatar,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import { useParams, useNavigate } from "react-router-dom";
import "./CompanyPage.css";
import CompanySummary from "./CompanySummary.js";
import getTenantIdFromURL from "./utils/getTenantIdFromURL.js";
import { useSelector } from "react-redux";
import ExecutiveSummary from "./ExecutiveSummary.js";
import getExecutiveData from "./getExecutiveData.js";
import CompanySummaryEvents from "./companySummaryEvents";
import CompanyFinancialInfo from "./CompanyFinancialInfo.js";
import QuestionsAccordion from "./QuestionsAccordion.js";
import ContextOverview from "./components/ContextOverview.js";
import { isUnderwritingActive } from "./utils/Helper";
import CompanyTree from "./CompanyTree.js";
import yellowBulb from "./Assets/Images/bulb.svg";
import redBulb from "./Assets/Images/redBulb.svg";
import greenBulb from "./Assets/Images/greenBulb.svg";
import Logs from './Logs';
import { getCompanySummary, getCompanyNews, getCompanyTimelineEvents, getAggregatedData, getAlternativeData, createAlternativeData, deleteCompanySummary, deleteAggregatedData, deleteAlternativeData, getCompanyQuestions, generateCompanyReport, getFinancialData, getDnbFinancialEligibility, importDnbFinancialStatements, updateCompanySummary, generateAiSummary } from "./api/underwritingApi";
import { deleteCompany, startUnderwritingFlow } from "./api/companyApi";
import { getFreezeMessage } from "./utils/refreshFreeze";
import SimpleNews from "./SimpleNews.js";
import CompanyEventTimeline from "./CompanyEventTimeline/CompanyEventTimeline.js";
import VerifyEntityModal from "./VerifyEntityModal.js";
import UserNotes from "./UserNotes";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ReportViewer from "./MarkdownReport.js";
import FinancialReport from "./FinancialReport.js";
import { toast } from "react-toastify";
import { useRolePermissions } from "./context/RolePermissionsContext";
import { RestrictedAction } from "./components/RestrictedAction";
import ChatFile from "./ChatFile.js";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckIcon from '@mui/icons-material/Check';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import { Dialog, DialogTitle, DialogContent, DialogActions, Menu, MenuItem, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BrandThinkingLoader from './components/BrandThinkingLoader';
import { sendGeneralEmail, getUserInfoBySubId, sendApprovalEmail, logApproval, getTenantById } from './api/tenantApi';
import { getInsuranceRequests } from './api/insuranceApi';
import { buildInsuredLimitMap, insuredLimitFor } from './utils/insuredLimit';
import DunsControlStatus from "./components/DunsControlStatus.js";
import CompanyNewsResearch from "./components/CompanyNewsResearch.js";
import AssessmentReview from "./components/AssessmentReview.js";
import PathToRequestedLimitCard from "./components/PathToRequestedLimitCard.js";
import { startAssessmentReview, getReviewStatus, pushReviewAssessment } from "./api/claudeApi";
import { resolveRouteTenantId } from "./utils/tenantRouting";
import { getFinancialReportLocalDisabledReason, getFinancialReportDisabledReason, isManualFinancialUploadOnly } from './utils/financialReportAccess';
import { getCurrentUserId } from './authUtils';

// CSS animation for spinning refresh icon
const styles = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);


const formatDiagnosticJson = (value) => {
  if (value === undefined || value === null) return 'No response received.';
  try {
    return JSON.stringify(value, null, 2);
  } catch (_) {
    return String(value);
  }
};

// Helper to parse Firestore Timestamp, string, or number to Date
function parseFirestoreTimestamp(ts) {
  if (!ts) return null;
  // Firestore Timestamp object
  if (typeof ts === 'object' && typeof ts.toDate === 'function') {
    return ts.toDate();
  }
  // If it's a seconds+nanoseconds object
  if (typeof ts === 'object' && ts.seconds) {
    return new Date(ts.seconds * 1000);
  }
  // If it's a string or number
  const date = new Date(ts);
  return isNaN(date.getTime()) ? null : date;
}

function CompanyPage({ adminAction }) {
  const params = useParams();
  const navigate = useNavigate();
  
  // Extract company ID from URL params as fallback
  const companyIdFromUrl = params.companyId || params.id;
  const effectiveCompanyId = companyIdFromUrl;

  // All React hooks must be called before any early returns
  const [companyOverviewData, setCompanyOverviewData] = useState(null);
  const [companyBalanceSheetData, setCompanyBalanceSheetData] = useState(null);
  const [companyIncomeStatementData, setCompanyIncomeStatementData] = useState(null);
  const [dnBFinancialV2Data, setDnBFinancialV2Data] = useState(null);
  const [financialStatementsData, setFinancialStatementsData] = useState(null);
  const [tenantId, setTenantId] = useState();
  const [isInsuranceEnabled, setIsInsuranceEnabled] = useState(false);
  const [insuredLimit, setInsuredLimit] = useState(null);
  const isAdmin = useSelector((state) => state.userInfo.isAdmin);
  const isDistributor = useSelector((state) => state.userInfo.isDistributor);
  const { permissions, loading: permissionsLoading, isSupportPreview } = useRolePermissions();
  const subId = localStorage.getItem("subId");

  const URLTenant = getTenantIdFromURL();
  const currentTenant = useSelector((state) => state.userInfo.tenantId);
  const [selectedTab, setSelectedTab] = useState(0);
  const [agingData, setAgingData] = useState(undefined);
  const [linkedInJSON, setLinkedInJSON] = useState({});
  const [DnbData, setDnbData] = useState();
  const [currentCompany, setCurrentCompany] = useState({});
  const [summaryLoadFailed, setSummaryLoadFailed] = useState(false);
  const [aiSummaryGenerating, setAiSummaryGenerating] = useState(false);
  const [aiSummaryFailed, setAiSummaryFailed] = useState(false);
  const [aiSummaryError, setAiSummaryError] = useState("");
  const [companySummary, setCompanySummary] = useState({});
  const [dnbJsonData, setDnbJsonData] = React.useState([]);
  const [showExport, setShowExport] = React.useState(false);
  const [alerts, setAlerts] = useState([]);
  const [latestAlert, setLatestAlert] = useState("No new alerts!");
  const [bulbColor, setBulbColor] = React.useState(yellowBulb);
  const [events, setEvents] = React.useState([]);
  const [newsData, setNewsData] = useState([]);
  const [timelineData, setTimelineData] = useState([]);
  const [financialData, setFinancialData] = useState(null);
  console.log("🚀 ~ financialData:", financialData)
  const [resolvedData, setResolvedData] = useState(null);
  const [showUserNotes, setShowUserNotes] = useState(false);
  const [isRefreshingAlternativeData, setIsRefreshingAlternativeData] = useState(false);
  const [alternativeData, setAlternativeData] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isUnderwriting, setIsUnderwriting] = useState(false); // drives the "underwriting in progress" bar
  const underwritingBaselineRef = useRef(null); // updatedAt snapshot to detect completion
  const [isExporting, setIsExporting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [approversOpen, setApproversOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);
  const [anchorElActions, setAnchorElActions] = useState(null);
  const openActions = Boolean(anchorElActions);
  const handleActionsClick = (e) => setAnchorElActions(e.currentTarget);
  const handleActionsClose = () => setAnchorElActions(null);
  const userNotesRef = useRef(null);
  const [userInfo, setUserInfo] = useState(null);
  const [tenantName, setTenantName] = useState(null);
  const [dnbImportOpen, setDnbImportOpen] = useState(false);
  const [isImportingDnb, setIsImportingDnb] = useState(false);
  const [dnbImportResult, setDnbImportResult] = useState(null);
  const [dnbImportError, setDnbImportError] = useState('');
  const [dnbDiagnostics, setDnbDiagnostics] = useState(null);
  const [financialReportRefreshKey, setFinancialReportRefreshKey] = useState(0);
  const [financialReportTenant, setFinancialReportTenant] = useState({});
  const [financialAccessRevision, setFinancialAccessRevision] = useState(0);
  const [financialEligibility, setFinancialEligibility] = useState({});
  const dnbImportPendingRef = useRef(false);
  const financialAccessRef = useRef(null);
  const financialAccessMountedRef = useRef(false);
  const financialRequesterId = useSelector((state) => state.userInfo.subId) || getCurrentUserId();
  const financialScopeTenantId = resolveRouteTenantId({ isAdmin, isDistributor, urlTenant: URLTenant, currentTenant });
  const financialTenantMatches = financialReportTenant.tenantId === financialScopeTenantId &&
    financialReportTenant.revision === financialAccessRevision;
  const financialLocalAccess = {
    tenantId: financialScopeTenantId,
    companyId: effectiveCompanyId,
    requesterId: financialRequesterId,
    tenant: financialTenantMatches ? financialReportTenant.data : null,
    tenantLoading: !financialTenantMatches || financialReportTenant.loading,
    tenantError: financialTenantMatches && financialReportTenant.error,
    permissions,
    permissionsLoading,
    isSupportPreview,
  };
  const financialLocalDisabledReason = getFinancialReportLocalDisabledReason(financialLocalAccess);
  const showProviderFinancials = !isManualFinancialUploadOnly(financialLocalAccess);
  const financialEligibilityRequestKey = JSON.stringify([
    financialScopeTenantId, effectiveCompanyId, financialRequesterId, financialAccessRevision, financialLocalDisabledReason,
  ]);
  const financialEligibilityMatches = financialEligibility.requestKey === financialEligibilityRequestKey;
  const financialReportDisabledReason = getFinancialReportDisabledReason({
    ...financialLocalAccess,
    eligibility: financialEligibilityMatches ? financialEligibility.data : null,
    eligibilityLoading: !financialEligibilityMatches || financialEligibility.loading,
    eligibilityError: financialEligibilityMatches && financialEligibility.error,
  });
  // A callback retained by a child must not bypass a newer scope or access restriction.
  financialAccessRef.current = { requestKey: financialEligibilityRequestKey, disabledReason: financialReportDisabledReason };
  const canRetryFinancialAccess = showProviderFinancials && Boolean(financialReportDisabledReason && financialRequesterId) && !permissionsLoading &&
    !isSupportPreview && permissions?.isViewOnly !== true && permissions?.canEditEntity === true &&
    permissions?.canViewFinancialData === true && !financialLocalAccess.tenantLoading &&
    financialEligibilityMatches && !financialEligibility.loading;

  useEffect(() => {
    financialAccessMountedRef.current = true;
    return () => { financialAccessMountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (showUserNotes && userNotesRef.current) {
      setTimeout(() => {
        userNotesRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [showUserNotes]);
  // Add a ref to track if data has been fetched
  const hasFetchedData = useRef(false);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const lastCompanyId = useRef(null);
  // AI assessment review is expensive. Only re-run it after an explicit underwriting refresh,
  // never merely because the report page was opened.
  const pendingAiReviewAfterRefreshRef = useRef(false);
  const aiSummaryBaselineRef = useRef(null);

  // Reset data fetching when company ID changes
  useEffect(() => {
    if (lastCompanyId.current !== effectiveCompanyId) {
      console.log("🔄 Company ID changed from", lastCompanyId.current, "to", effectiveCompanyId);
      lastCompanyId.current = effectiveCompanyId;
      hasFetchedData.current = false;
      setIsInitialLoadComplete(false);
      setCurrentCompany({}); // Clear previous company data
    }
  }, [effectiveCompanyId]);

  useEffect(() => {
    // Initialize tenant ID immediately
    setTenantId(resolveRouteTenantId({
      isAdmin,
      isDistributor,
      urlTenant: URLTenant,
      currentTenant,
    }));
  }, [isAdmin, isDistributor, URLTenant, currentTenant]);

  // Resolve the tenant's human-readable name (used to gate tenant-specific UI).
  useEffect(() => {
    let cancelled = false;
    setFinancialReportTenant({ tenantId, revision: financialAccessRevision, loading: true });
    if (!tenantId) {
      setTenantName(null);
      return;
    }
    getTenantById(tenantId)
      .then((tenant) => {
        if (!cancelled) {
          setFinancialReportTenant({ tenantId, revision: financialAccessRevision, data: tenant?.data, loading: false });
          setTenantName(tenant?.data?.name || null);
          setIsInsuranceEnabled(!!tenant?.data?.isInsuranceEnabled);
        }
      })
      .catch((err) => {
        if (!cancelled) setFinancialReportTenant({ tenantId, revision: financialAccessRevision, loading: false, error: true });
        console.error("Error fetching tenant name:", err);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, financialAccessRevision]);

  // This endpoint only checks access; it does not contact D&B or generate a report.
  useEffect(() => {
    let cancelled = false;
    if (financialLocalDisabledReason) {
      setFinancialEligibility({ requestKey: financialEligibilityRequestKey });
      return;
    }
    setFinancialEligibility({ requestKey: financialEligibilityRequestKey, loading: true });
    getDnbFinancialEligibility(financialScopeTenantId, effectiveCompanyId)
      .then((data) => {
        if (!cancelled) setFinancialEligibility({ requestKey: financialEligibilityRequestKey, data, loading: false });
      })
      .catch((error) => {
        if (!cancelled) setFinancialEligibility({ requestKey: financialEligibilityRequestKey, error, loading: false });
      });
    return () => { cancelled = true; };
  }, [financialScopeTenantId, effectiveCompanyId, financialLocalDisabledReason, financialEligibilityRequestKey]);

  useEffect(() => {
    setDnbImportOpen(false);
    setDnbImportResult(null);
    setDnbImportError('');
    setDnbDiagnostics(null);
  }, [financialScopeTenantId, effectiveCompanyId, financialRequesterId]);

  // When the tenant has the insurance layer, resolve this company's insured limit (its active
  // cover limit) so the report can show it; null → "-" (no carrier connected).
  useEffect(() => {
    let cancelled = false;
    if (!tenantId || !effectiveCompanyId || !isInsuranceEnabled) { setInsuredLimit(null); return; }
    getInsuranceRequests(tenantId)
      .then((res) => {
        if (cancelled) return;
        const map = buildInsuredLimitMap(res?.data || []);
        setInsuredLimit(insuredLimitFor(map, effectiveCompanyId));
      })
      .catch(() => { if (!cancelled) setInsuredLimit(null); });
    return () => { cancelled = true; };
  }, [tenantId, effectiveCompanyId, isInsuranceEnabled]);

  // Consolidated data fetching - runs only once when tenantId and effectiveCompanyId are available
  useEffect(() => {
    const fetchAllData = async () => {
      if (tenantId && effectiveCompanyId && !hasFetchedData.current) {
        console.log("🚀 Starting initial data fetch for:", { tenantId, effectiveCompanyId });
        hasFetchedData.current = true;
        
        try {
          // Fetch all data in parallel for better performance
          await Promise.allSettled([
            getSummaryData(),
            fetchNewsData(),
            fetchAlternativeData(),
            fetchQuestions(),
            fetchFinancialData()
          ]);

          console.log("✅ All initial data fetched successfully");
          setIsInitialLoadComplete(true);
          
        } catch (error) {
          console.error("❌ Error in initial data fetch:", error);
          // Reset flag on error so it can be retried
          hasFetchedData.current = false;
        }
      }
    };
    
    fetchAllData();
  }, [tenantId, effectiveCompanyId]); // Added effectiveCompanyId to dependencies


  const reFetchSummaryData = async () => {
    await getSummaryData();
  };

  // Separate useEffect for timeline data that depends on currentCompany
  useEffect(() => {
    const fetchTimelineData = async () => {
      if (tenantId && effectiveCompanyId && currentCompany?.companyName && isInitialLoadComplete) {
        try {
          console.log("✅ Calling fetchTimelineData");
          const timelineResponse = await getCompanyTimelineEvents(tenantId, effectiveCompanyId, currentCompany.companyName);
          
          if (timelineResponse?.data?.items) {
            setTimelineData(timelineResponse.data.items);
          } else {
            setTimelineData([]);
          }
        } catch (error) {
          console.error("Error fetching timeline data:", error);
          setTimelineData([]);
        }
      }
    };

    fetchTimelineData();
  }, [currentCompany, tenantId, effectiveCompanyId, isInitialLoadComplete]);
  
  useEffect(() => {
    const fetchUserInfo = async () => {
      if (subId) {
        try {
          const info = await getUserInfoBySubId(subId);
          setUserInfo(info);
          console.log('Fetched userInfo:', info);
        } catch (err) {
          console.error('Error fetching userInfo:', err);
        }
      }
    };
    fetchUserInfo();
  }, [subId]);

  // Get additional info from CompanySummary object
  useEffect(() => {
    if (currentCompany?.summaryData?.additionalInfo) {
      setAdditionalInfo(currentCompany?.summaryData?.additionalInfo);
    }
  }, [currentCompany]);

  useEffect(() => {
    const fetchUserInfo = async () => {
      if (subId) {
        try {
          const info = await getUserInfoBySubId(subId);
          setUserInfo(info);
          console.log('Fetched userInfo:', info);
        } catch (err) {
          console.error('Error fetching userInfo:', err);
        }
      }
    };
    fetchUserInfo();
  }, [subId]);

  // Get additional info from CompanySummary object
  useEffect(() => {
    if (currentCompany?.summaryData?.additionalInfo) {
      setAdditionalInfo(currentCompany?.summaryData?.additionalInfo);
    }
  }, [currentCompany]);

  const fetchFinancialData = async () => {
    if (effectiveCompanyId) {
      try {
        const result = await getFinancialData(effectiveCompanyId);
        console.log("📊 Financial data fetched:", financialData);
        setFinancialData(result?.data);
      } catch (error) {
        console.error("Error fetching financial data:", error);
        setFinancialData(null);
      }
    }
  }

  var financialInfo = { last3yrRevenue: null };
  var summaryScores = {
    ARCScore: "",
    paymentIndex: "",
    legalScore: "",
    alternativeScore: "",
    countryScore: "",
    riskScore: "",
    sectorScore: "",
    recommendedLimit: "",
    requestedLimit: "",
    underwritingReason: "",
    currentArBalance: "",
    totalArBalance: "",
    termsRecommended: "",
    currentRecommended: "",
    paydex: "",
    tenure: "",
    avgCustomerPaymentDays: "",
    arear: "-",
    delinquencyScore: "",
    failureScore: "",
    altmanZScore: "",
    arBalance30: "",
    arBalance60: "",
    arBalance90: "",
    lastUpdatedDate: "",
    isAgingCompany: false,
  };

  var executiveData = {
    Description: "",
    Main_sector: "",
    Number_of_employees: "",
    Market_cap: "",
    Credit_Rating: "",
    Founders: "",
    Key_staff: "",
    Main_location_office: "",
    Revenue: "",
    Symbol: "",
    Founded: "",
    Parent_company: "",
    Website: "",
    Main_competitors: "",
    Social_media: "",
    Email_address: "",
    Company_phone: "",
  };

  if (currentCompany && Object.keys(currentCompany).length > 0) {
    try {
      const data = getExecutiveData({
        company: currentCompany,
        summaryRecord: currentCompany,
        overview: companyOverviewData,
        incomeStatement: companyIncomeStatementData,
        balanceSheet: companyBalanceSheetData,
        AgingDataForCompany: agingData,
        dnBFinancialV2Data: dnBFinancialV2Data,
      });
      ({ summaryScores, executiveData, financialInfo } = data);
      } catch (error) {
      console.log("Error in getExecutiveData:", error);
    }
  }

  // Complete entity context handed to the Copilot and the Assessment Review —
  // everything available on the page, so Claude never reviews partial data.
  // Explicit, compact record of the Claude limit fallback so the Underwriting Copilot can answer
  // "did the AI/Claude fallback run, and what limit did it suggest?". Prefers the authoritative
  // "AI Limit Fallback" object the underwriting engine now persists; for older assessments it
  // infers from the persisted signals (Recommended Net = Net 180 + the underwriting reason).
  const aiLimitFallback = (() => {
    const sd = currentCompany?.summaryData || {};
    const explicit = sd["AI Limit Fallback"];
    if (explicit && typeof explicit === "object") return explicit;
    const recNet = sd["Recommended Net"];
    const uwReason = sd["Underwriting Reason"] || "";
    const recLimit = sd["Recommended Limit"];
    const isAiEstimate = /conservative ai estimate/i.test(uwReason) || recNet === "Net 180";
    if (isAiEstimate) {
      return {
        ran: true, produced: true, model: "AIArc",
        valueUSD: recLimit, terms: recNet || "Net 180", basis: uwReason || null,
        note: "Inferred from the persisted signals (Recommended Net = Net 180 and the underwriting reason). The Recommended Limit shown is the conservative limit AIArc suggested under NET 180 terms.",
      };
    }
    return {
      ran: false, model: "AIArc",
      note: "No AIArc limit-fallback signal on this assessment. The Recommended Limit is from CrediArc's standard model, or the fallback did not run (it only runs when ARC < 8 and the standard model produced a $0 limit).",
    };
  })();

  const assessmentEntity = {
    aiLimitFallback,
    // Business profile / description / sector / revenue / website etc. live in
    // resolvedData ({ key: { value, source } }), NOT executiveData (which
    // getExecutiveData does not return). Pass resolvedData so Claude actually
    // sees the description and profile fields shown on the page.
    businessProfile: resolvedData,
    underwritingScores: summaryScores,
    financialInfo: financialData,
    financialStatements: financialStatementsData,
    incomeStatement: companyIncomeStatementData,
    balanceSheet: companyBalanceSheetData,
    companyOverview: companyOverviewData,
    dnbFinancialV2: dnBFinancialV2Data,
    agingData: agingData,
    controlStatus: currentCompany?.dunsControlStatus,
    monitoringEvents: timelineData,
    news: newsData,
    companyTree: currentCompany?.companyTreeData,
    ownershipLinkedIn: linkedInJSON,
    dnbData: dnbJsonData,
    alternativeData: alternativeData,
    questions: questions,
    summaryData: currentCompany?.summaryData,
    approvalStatus: currentCompany?.approvalStatus,
    approvers: currentCompany?.approvers,
    analystNotes: currentCompany?.userNotes,
    tenantId,
    companyId: effectiveCompanyId,
  };

  // Re-run a previously-pushed AI assessment only when an explicit underwriting refresh
  // requested it. Opening or revisiting the report page must only read the saved result.
  useEffect(() => {
    if (!pendingAiReviewAfterRefreshRef.current) return;
    if (!currentCompany?.aiReviewPushed || !isAdmin) return;
    if (!tenantId || !effectiveCompanyId) return;
    pendingAiReviewAfterRefreshRef.current = false;
    const entity = assessmentEntity;
    const name = currentCompany?.name || currentCompany?.companyName;
    (async () => {
      const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
      try {
        toast.info("Re-applying AI assessment…", { autoClose: 4000, toastId: "auto-ai" });
        const start = await startAssessmentReview({ tenantName, entityName: name, entity, allowWeb: true });
        const jobId = start?.jobId;
        if (!jobId) return;
        let report = null;
        for (let i = 0; i < 60; i++) {
          await sleep(4000);
          const st = await getReviewStatus(jobId);
          if (st?.status === "done") { report = st.report; break; }
          if (st?.status === "error") return;
        }
        if (!report) return;
        const sa = report.suggestedAssessment || {};
        const summary = (sa.rationale || "").replace(/\s+/g, " ").trim();
        const firstSentence = (summary.match(/^.*?[.!?](\s|$)/)?.[0] || summary).trim();
        const shortReason = ((sa.rationaleShort || firstSentence || sa.recommendation || "").replace(/\s+/g, " ").trim().slice(0, 220)) || "AI-suggested assessment";
        await pushReviewAssessment({
          tenantId,
          companyId: effectiveCompanyId,
          arcScore: sa.riskBand,
          recommendedLimit: sa.creditLimit,
          underwritingReason: shortReason,
          rationale: summary,
          suggestedRevenue: report.suggestedRevenue?.valueUSD,
        });
        await getSummaryData();
        toast.success("AI assessment re-applied.", { toastId: "auto-ai-done" });
      } catch (e) {
        /* silent — refresh-triggered background step */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany?.aiReviewPushed, currentCompany?.updatedAt, currentCompany?.aiSummaryRefreshCompletedAt, isAdmin, tenantId, effectiveCompanyId]);

  const getSummaryData = async () => {
    console.log("🔍 getSummaryData called:");
    console.log("tenantId:", tenantId);
    console.log("effectiveCompanyId:", effectiveCompanyId);
    
    if (tenantId && effectiveCompanyId) {
      // Retry a few times: a transient failure (e.g. a 503 while the underwriting
      // service rolls out a deploy) must NOT strand the page on "Loading company
      // data…" forever. On total failure we surface a Retry button (see the loading
      // gate) instead of an infinite spinner.
      let lastErr = null;
      for (let attempt = 1; attempt <= 4; attempt++) {
        try {
          console.log(`🚀 getCompanySummary attempt ${attempt}`, { tenantId, companyId: effectiveCompanyId });
          const summary = await getCompanySummary(tenantId, effectiveCompanyId);
          if (summary && Object.keys(summary).length > 0) {
            setCurrentCompany(summary);
            if (summary?.resolvedData) setResolvedData(summary.resolvedData);
            setSummaryLoadFailed(false);
            return { summary };
          }
          lastErr = new Error("Empty company summary");
        } catch (error) {
          lastErr = error;
          console.log(`getCompanySummary attempt ${attempt} failed:`, error?.message || error);
        }
        if (attempt < 4) await new Promise((r) => setTimeout(r, 2000));
      }
      console.error("getCompanySummary failed after retries:", lastErr?.message || lastErr);
      setSummaryLoadFailed(true);
      hasFetchedData.current = false; // let a manual Retry re-run the whole fetch
      return null;
    } else {
      console.log("❌ Missing tenantId or effectiveCompanyId:", { tenantId, effectiveCompanyId });
    }
  };

  // Underwriting completion can arrive while this page has no active WebSocket
  // listener.  When the backend records a newly generated summary, fetch that
  // authoritative version once rather than leaving the user on the stale
  // "isn't available yet" state.
  useEffect(() => {
    if (!tenantId || !effectiveCompanyId || currentCompany?.aiSummaryText || !currentCompany?.aiSummaryAt) return undefined;
    let cancelled = false;
    const refreshTimer = setTimeout(async () => {
      try {
        const latest = await getCompanySummary(tenantId, effectiveCompanyId);
        if (!cancelled && latest?.aiSummaryText) {
          setCurrentCompany(latest);
          if (latest?.resolvedData) setResolvedData(latest.resolvedData);
        }
      } catch (error) {
        // The normal load and manual Generate-with-AI controls remain available.
        console.warn('Unable to refresh completed AI summary:', error?.message || error);
      }
    }, 1000);
    return () => {
      cancelled = true;
      clearTimeout(refreshTimer);
    };
  }, [tenantId, effectiveCompanyId, currentCompany?.aiSummaryText, currentCompany?.aiSummaryAt]);

  // Explicitly generate and persist a missing AI company summary.
  const generateSummaryNow = async () => {
    if (!tenantId || !effectiveCompanyId) return;
    setAiSummaryFailed(false);
    setAiSummaryError("");
    setAiSummaryGenerating(true);
    try {
      // Kick off generation (returns 202 immediately — it runs in the background and saves
      // when done, since grounded generation may exceed the gateway timeout).
      await generateAiSummary(tenantId, effectiveCompanyId);
      // Poll the summary until the generated aiSummaryText shows up (or we give up).
      const deadline = Date.now() + 130000; // ~2 min
      let fresh = null;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 5000));
        const s = await getCompanySummary(tenantId, effectiveCompanyId).catch(() => null);
        if (s?.aiSummaryText) { fresh = s; break; }
      }
      // Set the full fresh summary so the exec-box description (resolvedData) updates too.
      if (fresh) setCurrentCompany(fresh);
      else { setAiSummaryFailed(true); setAiSummaryError("Still generating — please try again in a moment."); }
    } catch (e) {
      console.error("AI summary generation failed:", e);
      setAiSummaryFailed(true);
      setAiSummaryError(e?.response?.data?.message || e?.message || "Generation failed.");
    } finally {
      setAiSummaryGenerating(false);
    }
  };

  // Manual retry from the loading/error screen (no full page reload needed).
  const retryLoadCompany = () => {
    setSummaryLoadFailed(false);
    hasFetchedData.current = false;
    getSummaryData();
    fetchQuestions();
    fetchFinancialData();
  };

  const fetchNewsData = async () => {
    if (tenantId && effectiveCompanyId) {
      try {
        console.log("🔍 Fetching news data for:", { tenantId, effectiveCompanyId });
        const newsResponse = await getCompanyNews(tenantId, effectiveCompanyId);
        console.log("📰 News data fetched:", newsResponse);
        
        if (newsResponse?.data?.items) {
          setNewsData(newsResponse.data.items);
        } else {
          setNewsData([]);
        }
      } catch (error) {
        console.error("Error fetching news data:", error);
        setNewsData([]);
      }
    }
  };

  const fetchAlternativeData = async () => {
    if (effectiveCompanyId) {
      try {
        console.log("🔍 Fetching alternative data for:", { effectiveCompanyId });
        const altDataResponse = await getAlternativeData(effectiveCompanyId);
        console.log("📊 Alternative data fetched:", altDataResponse);
        
        if (altDataResponse?.success && altDataResponse?.data) {
          setAlternativeData(altDataResponse.data);
        } else {
          setAlternativeData(null);
        }
      } catch (error) {
        console.error("Error fetching alternative data:", error);
        setAlternativeData(null);
      }
    }
  };

  // While underwriting is in progress (triggered here, or already running when the page
  // loaded), poll for completion, then refresh the page data so the new ARC score / limit
  // / summary appear automatically. The spinner/bar must stay up for the ENTIRE
  // underwriting and stop only when it truly ends. So completion requires that we
  // have actually SEEN the backend flag go true (underwriting really started) and
  // then clear — not just a stale-baseline updatedAt change on the first tick, which
  // would false-complete right as the "started" toast fades. Applies to all tenants.
  useEffect(() => {
    const inProgress = isUnderwriting || isUnderwritingActive(currentCompany);
    if (!inProgress || !tenantId || !effectiveCompanyId) return;
    if (underwritingBaselineRef.current === null) {
      underwritingBaselineRef.current = JSON.stringify(currentCompany?.updatedAt ?? null);
    }
    // Track, for this in-progress cycle only, whether the backend has reported the
    // underwriting as actually running at least once.
    let sawInProgress = isUnderwritingActive(currentCompany);
    const startedAt = Date.now();
    let active = true;
    const iv = setInterval(async () => {
      try {
        const s = await getCompanySummary(tenantId, effectiveCompanyId);
        if (!active || !s) return;
        if (isUnderwritingActive(s)) sawInProgress = true;
        const elapsed = Date.now() - startedAt;
        const changed = JSON.stringify(s.updatedAt ?? null) !== underwritingBaselineRef.current;
        // Primary signal: the flag was seen running and has now cleared => truly done.
        const flagDone = sawInProgress && !isUnderwritingActive(s);
        // Fallback: if the backend never surfaced the flag (write race), fall back to
        // the summary being rewritten — but never in the first 60s, so an early
        // intermediate write can't stop the spinner right after start.
        const fallbackDone = !sawInProgress && changed && elapsed > 60000;
        // Never complete in the first 10s regardless (the "started" toast is still up).
        // Full refreshes also regenerate the persisted AI summary. Do not declare the refresh
        // complete on the earlier score write; wait for the summary timestamp to change. If the
        // provider fails, release the UI after three minutes and preserve the prior saved summary.
        const summaryWasRegenerated = JSON.stringify(s.aiSummaryRefreshCompletedAt ?? null) !== aiSummaryBaselineRef.current;
        const summaryWaitExpired = elapsed > 180000;
        if ((flagDone || fallbackDone) && elapsed > 10000 && (summaryWasRegenerated || summaryWaitExpired)) {
          clearInterval(iv);
          underwritingBaselineRef.current = null;
          setIsUnderwriting(false);
          setCurrentCompany(s);
          await getSummaryData();
          fetchNewsData();
          fetchAlternativeData();
          toast.success("Underwriting complete.", { toastId: "uw-done" });
        }
      } catch (e) { /* keep polling */ }
    }, 6000);
    const to = setTimeout(() => {
      clearInterval(iv);
      setIsUnderwriting(false);
      underwritingBaselineRef.current = null;
    }, 720000); // 12-min safety cap
    return () => { active = false; clearInterval(iv); clearTimeout(to); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUnderwriting, currentCompany?.underwritingInProgress, tenantId, effectiveCompanyId]);

  // Early return if no company ID is available yet. MUST stay below every hook
  // (including the auto-re-review and underwriting-poll effects above) so React's
  // rules-of-hooks are respected — otherwise the production build fails to compile.
  if (!effectiveCompanyId) {
    return <BrandThinkingLoader minHeight={420} />;
  }

  const fetchQuestions = async () => {
    if (tenantId && effectiveCompanyId) {
      try {
        console.log("🔍 Fetching questions for:", { tenantId, effectiveCompanyId });
        const questionsResponse = await getCompanyQuestions(tenantId, effectiveCompanyId);
        console.log("❓ Questions data fetched:", questionsResponse);
        
        if (questionsResponse?.success && questionsResponse?.data) {
          setQuestions(questionsResponse.data);
        } else {
          setQuestions([]);
        }
      } catch (error) {
        console.error("Error fetching questions:", error);
        setQuestions([]);
      }
    }
  };

  const refreshAlternativeData = async () => {
    try {
      setIsRefreshingAlternativeData(true);
      console.log("🔄 Refreshing alternative data for:", { tenantId, effectiveCompanyId });
      
      // Call the create alternative data API
      const params = {
        companyName: currentCompany?.name || currentCompany?.companyName,
        address: currentCompany?.address,
        country: currentCompany?.country,
        tenantId: tenantId,
        id: effectiveCompanyId
      };
      
      const result = await createAlternativeData(params);
      
      if (result?.success) {
        // Update the alternative data state with the new data
        setAlternativeData(result.data);
        toast.success("Alternative data refreshed successfully");
      } else {
        toast.error("Failed to refresh alternative data");
      }
    } catch (error) {
      console.error("Error refreshing alternative data:", error);
      toast.error("Failed to refresh alternative data");
    } finally {
      setIsRefreshingAlternativeData(false);
    }
  };

  // Add a function to reset data fetching (for manual refresh)
  const resetDataFetching = () => {
    hasFetchedData.current = false;
    setIsInitialLoadComplete(false);
  };

  // Update handleRefresh to fire and forget
  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      console.log("🔄 Starting underwriting flow for:", { tenantId, effectiveCompanyId });

      // Guard: skip if this company's automatic refresh is frozen
      const freezeBlock = getFreezeMessage(currentCompany);
      if (freezeBlock) {
        toast.warning(freezeBlock);
        return;
      }

      // Call the new company-service endpoint
      pendingAiReviewAfterRefreshRef.current = !!currentCompany?.aiReviewPushed && isAdmin;
      aiSummaryBaselineRef.current = JSON.stringify(currentCompany?.aiSummaryRefreshCompletedAt ?? null);
      await startUnderwritingFlow(tenantId, effectiveCompanyId);

      // Show the progress bar right away (the backend flag is set async, and this page's
      // currentCompany is stale), and snapshot updatedAt so the poll can detect completion.
      underwritingBaselineRef.current = JSON.stringify(currentCompany?.updatedAt ?? null);
      setIsUnderwriting(true);

      toast.success('Underwriting flow started!', {
        bodyStyle: {
          fontWeight: "bold",
        },
      });
      
    } catch (error) {
      pendingAiReviewAfterRefreshRef.current = false;
      aiSummaryBaselineRef.current = null;
      console.error('Error triggering underwriting flow:', error);
      toast.error('Failed to start underwriting flow');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleDeleteCompany = async () => {
    // Show confirmation dialog
    const confirmed = window.confirm(
      `Are you sure you want to delete "${currentCompany?.name || currentCompany?.companyName}"? This action will permanently delete all company data and cannot be undone.`
    );

    if (!confirmed) {
      return;
    }

    try {
      setIsDeleting(true);
      console.log("🗑️ Deleting company data for:", { tenantId, effectiveCompanyId });

      // Delete all company data in parallel
      const deletePromises = [
        deleteCompany(effectiveCompanyId),
        deleteCompanySummary(tenantId, effectiveCompanyId),
        deleteAggregatedData(effectiveCompanyId),
        deleteAlternativeData(effectiveCompanyId)
      ];

      await Promise.allSettled(deletePromises);

      toast.success("Company deleted successfully", {
        bodyStyle: {
          fontWeight: "bold",
        },
      });

      if (isAdmin) {
        navigate(
          `/tenant-dashboard/${tenantId}/company`,
        );
      } else {
        navigate(`/${tenantId}/company`);
      }

    } catch (error) {
      console.error("Error deleting company:", error);
      toast.error("Failed to delete company. Some data may still exist.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Approve action (now with logic)
  const handleApprove = async () => {
    setApproveDialogOpen(false);
    setAnchorEl(null);
    if (!currentCompany?.approvers || !subId) {
      toast.error("Approvers or user ID missing");
      return;
    }
    try {
      // 1. Update approvedAt for current user
      const updatedApprovers = currentCompany.approvers.map((approver) =>
        approver.userId === subId ? { ...approver, approvedAt: new Date().toISOString() } : approver
      );

      // 2. If at least one approver has signed, approval is satisfied (no need for all approvers to sign)
      const atLeastOneApproverSigned = updatedApprovers.some((a) => a.approvedAt);
      const newApprovalStatus = atLeastOneApproverSigned ? "APPROVED" : currentCompany.approvalStatus;

      // 3. Call updateCompanySummary
      await updateCompanySummary(tenantId, effectiveCompanyId, {
        ...currentCompany,
        approvers: updatedApprovers,
        approvalStatus: newApprovalStatus,
      });

      // 4. Update local state
      setCurrentCompany((prev) => ({
        ...prev,
        approvers: updatedApprovers,
        approvalStatus: newApprovalStatus,
      }));
      toast.success(newApprovalStatus === "APPROVED" ? "Company approved!" : "Your approval has been recorded.");

      // 4a. Write an Approval log entry for every tenant (independent of the
      // dlocal-only approval email below). Without this, NAPS and other tenants
      // never get an Approval entry on the Logs page or in exports.
      try {
        await logApproval(effectiveCompanyId, {
          recommendedLimit: currentCompany?.summaryData?.["Recommended Limit"],
          requestedLimit: currentCompany?.requestedCreditLimit,
          approvedLimit: currentCompany?.approvedLimit,
          arcScore: currentCompany?.summaryData?.["Combined Score"],
        });
      } catch (logError) {
        console.error("Failed to write Approval log:", logError);
      }

      if (tenantId === "80ca91bd-bc50-40c0-90b7-f0e65294d10e") {
        // 5. Send approval email
        // const toAddresses = ["rahoolitis@gmail.com", "michal@crediarc.com"]; // Test Emails
        const toAddresses = ["marfernandez@dlocal.com", "gvincenzo@dlocal.com"];
        try {
          await sendApprovalEmail({
            tenantId,
            companyId: effectiveCompanyId,
            companyName: currentCompany?.name || currentCompany?.companyName,
            userName: userInfo?.nickname,
            toAddresses,
            recommendedLimit: currentCompany?.summaryData?.["Recommended Limit"],
            requestedLimit: currentCompany?.requestedCreditLimit,
            approvedLimit: currentCompany?.approvedLimit,
            arcScore: currentCompany?.summaryData?.["Combined Score"]
          });
          toast.success("Approval email sent!");
        } catch (emailError) {
          console.error("Failed to send approval email:", emailError);
          toast.error("Failed to send approval email.");
        }
      }
      if (tenantId === "2ab5560a-591e-49c4-b21c-728422144237") {
        // const toAddresses = ["rahoolitis@gmail.com", "michal@crediarc.com"]; // Test Emails
        const toAddresses = ["codesymphony@crediarc.com"];
        try {
          await sendApprovalEmail({
            tenantId,
            companyId: effectiveCompanyId,
            companyName: currentCompany?.name || currentCompany?.companyName,
            userName: userInfo?.nickname,
            toAddresses,
            recommendedLimit: currentCompany?.summaryData?.["Recommended Limit"],
            requestedLimit: currentCompany?.requestedCreditLimit,
            approvedLimit: currentCompany?.approvedLimit,
            arcScore: currentCompany?.summaryData?.["Combined Score"]
          });
          toast.success("Approval email sent!");
        } catch (emailError) {
          console.error("Failed to send approval email:", emailError);
          toast.error("Failed to send approval email.");
        }
      }
    } catch (err) {
      toast.error("Failed to approve company");
      console.error(err);
    }
  };

  const handleSendApprovalRequestEmail = async (approver) => {
    try {
      const email = approver?.email;
      if (!email) {
        toast.error("No email found for this approver");
        return;
      }
      const companyName =
        currentCompany?.name || currentCompany?.companyName || "";
      const recommendedLimit =
        currentCompany?.summaryData?.["Recommended Limit"] || "-";
      const riskScore =
        currentCompany?.summaryData?.["General Risk Score"] || "-";
      const subject = `Approval Request: ${companyName}`;
      const currentUrl = window.location.href;
      const companyNameDisplay = companyName || "Company";
      const htmlMessage = `Dear ${approver.name || ""},<br><br>

        You have a pending approval for the following company:<br><br>

        <strong>Company Name:</strong> ${companyNameDisplay}<br>
        <strong>Recommended Limit:</strong> ${recommendedLimit}<br>
        <strong>Risk Score:</strong> ${riskScore}<br><br>

        Please <a href="${currentUrl}" style="color: #5186EC; text-decoration: none; font-weight: 600;">click here to review and approve</a>.<br><br>

        <em style="color: #666; font-size: 14px;">Note: You need to be logged in to access the link.</em><br><br>

        Thank you!`;
      await sendGeneralEmail({
        tenantId: tenantId,
        subject,
        message: htmlMessage,
        to: [email],
        logEventType: 'Approval Request',
        logData:{
          roleName: approver.roleName,
          name: approver.name,
          companyId: effectiveCompanyId
        }
      });

      // Record last email sent time
      try {
        // 1. Update lastEmailSentAt
        const updatedApprovers = currentCompany.approvers.map((a) =>
          a.userId === approver.userId
            ? { ...a, lastEmailSentAt: new Date().toISOString() }
            : a
        );

        // 3. Call updateCompanySummary
        await updateCompanySummary(tenantId, effectiveCompanyId, {
          ...currentCompany,
          approvers: updatedApprovers,
        });

        // 4. Update local state
        setCurrentCompany((prev) => ({
          ...prev,
          approvers: updatedApprovers,
        }));
      } catch (error) {}

      toast.success("Reminder email sent!");
    } catch (err) {
      toast.error("Failed to send email");
      console.error(err);
    }
  };

  function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
      <div
        role='tabpanel'
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}>
        {value === index && <Box p={3}>{children}</Box>}
      </div>
    );
  }

  function a11yProps(index) {
    return {
      id: `simple-tab-${index}`,
      "aria-controls": `simple-tabpanel-${index}`,
    };
  }

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };
  const handleExport = async () => {
    try {
      setIsExporting(true);
      if (!tenantId || !effectiveCompanyId) {
        toast.error("Missing tenant or company ID");
        // No finally here for early return, so set false
        setIsExporting(false); 
        return;
      }
      toast.info("Generating report...", { autoClose: false, toastId: "export" });
      const response = await generateCompanyReport(tenantId, effectiveCompanyId);
      const base64String = response.data;
      const pdfBlob = base64ToBlob(base64String, "application/pdf");
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${currentCompany?.companyName || "Company"}-Report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      toast.dismiss("export");
      toast.success("Report downloaded!");
    } catch (error) {
      toast.dismiss("export");
      toast.error("Failed to export report");
      console.error(error);
    } finally {
      setIsExporting(false);
    }
  };

  const base64ToBlob = (base64String, mimeType) => {
    // Add padding if needed
    const paddedBase64String = base64String.padEnd(
      base64String.length + ((4 - (base64String.length % 4)) % 4),
      "="
    );

    const byteCharacters = atob(paddedBase64String);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  };

  const handleGetFinancialStatements = async () => {
    const isCurrentAccess = () => financialAccessMountedRef.current &&
      financialAccessRef.current?.requestKey === financialEligibilityRequestKey &&
      !financialAccessRef.current?.disabledReason;
    if (!isCurrentAccess() || dnbImportPendingRef.current) return;
    dnbImportPendingRef.current = true;
    setDnbImportOpen(true);
    setDnbImportResult(null);
    setDnbImportError('');
    setDnbDiagnostics(null);
    setIsImportingDnb(true);
    try {
      const result = await importDnbFinancialStatements(financialScopeTenantId, effectiveCompanyId);
      if (!isCurrentAccess()) return;
      setDnbImportResult(result);
      setDnbDiagnostics(result?.diagnostics || null);
      if (result?.availability === 'unavailable') {
        toast.info(result.message || 'No verified financial statements are available for this company.');
        return;
      }
      setFinancialReportRefreshKey((value) => value + 1);
      if (result?.alternativeAssessment?.data) {
        setAlternativeData(result.alternativeAssessment.data);
      } else {
        const assessment = await getAlternativeData(effectiveCompanyId).catch(() => null);
        if (!isCurrentAccess()) return;
        setAlternativeData(assessment?.success ? assessment.data : null);
      }
      setSelectedTab(5);
      const assessmentMessage = result?.alternativeAssessment?.success
        ? ' Alternative-data assessment refreshed with the new financial statements.'
        : result?.alternativeAssessment?.status === 'processing'
          ? ' The assessment refresh is continuing in the background.'
        : ' Financials were saved; the alternative-data refresh needs retrying.';
      toast.success(`${result?.statements?.length || 0} financial statements added by CrediArc.${assessmentMessage}`);
    } catch (error) {
      if (!isCurrentAccess()) return;
      setDnbDiagnostics(error?.response?.data?.diagnostics || null);
      const message = error?.response?.status === 504
        ? 'The financial-data service timed out. Please try again.'
        : error?.response?.status === 403
          ? 'Financial report generation is not permitted for this account or tenant. Contact your platform administrator.'
        : error?.response?.status === 404
          ? 'The financial-report service or company is unavailable. Contact your platform administrator.'
        : 'CrediArc could not retrieve financial statements';
      setDnbImportError(message);
      toast.error(message);
    } finally {
      dnbImportPendingRef.current = false;
      if (financialAccessMountedRef.current) setIsImportingDnb(false);
    }
  };

 
  // Handle loading state
  if (!currentCompany || Object.keys(currentCompany).length === 0) {
    console.log("🔄 CompanyPage loading state:", { 
      hasCurrentCompany: !!currentCompany, 
      currentCompanyKeys: Object.keys(currentCompany || {}),
      effectiveCompanyId,
      tenantId,
      hasFetchedDataRef: hasFetchedData.current,
      isInitialLoadComplete
    });
    return (
      <div style={{ padding: "40px", textAlign: "center" }}>
        {summaryLoadFailed ? (
          <>
            <Typography variant="h6" sx={{ mb: 1, color: "#1A2A56" }}>Couldn't load company data</Typography>
            <Typography sx={{ color: "#64748b", mb: 2.5 }}>
              The service may be briefly unavailable (e.g. during a deploy). Please try again.
            </Typography>
            <Button variant="contained" onClick={retryLoadCompany} sx={{ textTransform: "none" }}>Retry</Button>
          </>
        ) : (
          <Typography variant="h6">Loading company data...</Typography>
        )}
      </div>
    );
  }

  return (
    <div
      className='CompanyPage'
      style={{ width: "100%", backgroundColor: "#ECEDF0" }}>

      <div>
        <Box
          style={{
            display: "flex",
            justifyContent: "space-between",
            flexDirection: "row",
            alignItems: "center",
            padding: "20px",
          }}
        >
          {/* Left: Logo and Name */}
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              minWidth: 0,
            }}
          >
            <Avatar
              sx={{ width: 50, height: 50, cursor: "pointer" }}
              src={
                currentCompany?.logo ||
                `https://www.google.com/s2/favicons?sz=64&domain=${resolvedData?.website?.value || ""}`
              }
            />
            <Tooltip title={currentCompany?.name || currentCompany?.companyName || ""} arrow placement="bottom-start">
              <Typography
                style={{
                  fontFamily: "Rubik, sans-serif",
                  fontWeight: "500",
                  paddingLeft: "20px",
                  paddingRight: "20px",
                  color: "#1A2A56",
                  maxWidth: "500px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  flexGrow: 1,
                  minWidth: 0,
                  fontSize: "clamp(18px, 4vw, 28px)",
                  cursor: "default",
                }}
              >
                {currentCompany?.name || currentCompany?.companyName || "Loading..."}
              </Typography>
            </Tooltip>
            <Box paddingTop={"0px"}>
              {(currentCompany?.summaryData?.Status === "Bankruptcy" || currentCompany?.summaryData?.Status === "Liquidation") ? (
                <Box
                  style={{
                    width: "fit-content", 
                    display: "flex",
                    alignItems: "center",
                    background: "#FFF1F1",
                    border: "1px solid #FFE0E0",
                    borderRadius: "8px",
                    padding: "8px 12px",
                  }}>
                  <div
                    style={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      backgroundColor: "#FF4D4D",
                      marginRight: "8px"
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#FF4D4D",
                    }}>
                    {currentCompany?.summaryData?.Status}
                  </Typography>
                </Box>
              ):null}
            </Box>
            {(() => {
              const approvers = Array.isArray(currentCompany?.approvers) ? currentCompany.approvers : [];
              // Only show approval UI if there are approvers
              if (approvers.length === 0) {
                return null;
              }
              
              return (
                <>
                  {currentCompany?.approvalStatus && (() => {
                    const st = currentCompany.approvalStatus;
                    // Premium status pill: soft two-tone gradient, a status dot with a
                    // colored halo, refined uppercase type, and a subtle lift.
                    const p = st === 'APPROVED'
                      ? { text: '#0E7A46', dot: '#18A957', bgFrom: '#EDF9F1', bgTo: '#DCF2E4', border: 'rgba(24,169,87,0.30)', halo: 'rgba(24,169,87,0.22)' }
                      : st === 'PENDING'
                      ? { text: '#9A6800', dot: '#F5A623', bgFrom: '#FFF9EC', bgTo: '#FCEFCE', border: 'rgba(245,166,35,0.32)', halo: 'rgba(245,166,35,0.28)' }
                      : { text: '#54617A', dot: '#94A3B8', bgFrom: '#F6F8FB', bgTo: '#EDF1F6', border: 'rgba(100,116,139,0.24)', halo: 'rgba(148,163,184,0.22)' };
                    const label = st === 'APPROVED' ? 'Approved' : st === 'PENDING' ? 'Pending' : st;
                    return (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginLeft: '20px',
                          padding: '6px 16px 6px 13px',
                          borderRadius: '999px',
                          fontFamily: 'Rubik, sans-serif',
                          fontWeight: 600,
                          fontSize: '12px',
                          lineHeight: 1,
                          letterSpacing: '0.07em',
                          textTransform: 'uppercase',
                          color: p.text,
                          background: `linear-gradient(135deg, ${p.bgFrom} 0%, ${p.bgTo} 100%)`,
                          border: `1px solid ${p.border}`,
                          boxShadow: '0 1px 2px rgba(16,24,40,0.05), 0 2px 8px rgba(16,24,40,0.04)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <span
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: p.dot,
                            boxShadow: `0 0 0 3px ${p.halo}`,
                          }}
                        />
                        {label}
                      </span>
                    );
                  })()}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {(() => {
                      const userApprover = approvers.find(a => a.userId === subId);
                      const hasApproved = !!userApprover?.approvedAt;
                      const isCompanyApproved = currentCompany?.approvalStatus === 'APPROVED';
                      
                      // If company is approved, only show info button
                      if (isCompanyApproved) {
                        return (
                          <IconButton
                            color="primary"
                            onClick={() => setApproversOpen(true)}
                            aria-label="Approval Info"
                          >
                            <InfoOutlinedIcon />
                          </IconButton>
                        );
                      }
                      
                      // If user has already approved or is not an approver, show info button
                      if (!userApprover || hasApproved) {
                        return (
                          <IconButton
                          color="primary"
                          onClick={() => setApproversOpen(true)}
                          aria-label="Approval Info"
                          >
                            <InfoOutlinedIcon />
                          </IconButton>
                        );
                      }
                      
                      // Show approve button only if company is not approved, user hasn't approved yet, and approval limit is set
                      const hasApprovalLimit = summaryScores?.approvedLimit && summaryScores.approvedLimit !== "" && summaryScores.approvedLimit !== "0";

                      return (
                        <>
                          <RestrictedAction allowed={permissions.canApprove}>
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<CheckIcon />}
                            endIcon={<ArrowDropDownIcon />}
                            onClick={hasApprovalLimit ? handleMenuClick : undefined}
                            disabled={!hasApprovalLimit}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 600,
                              opacity: hasApprovalLimit ? 1 : 0.6
                            }}
                            title={hasApprovalLimit ? undefined : "Approval limit must be set before approving"}
                          >
                            Approve
                          </Button>
                          </RestrictedAction>
                          <Menu anchorEl={anchorEl} open={openMenu} onClose={handleMenuClose}>
                            <MenuItem
                              onClick={() => {
                                setApproveDialogOpen(true);
                                handleMenuClose();
                              }}
                              disabled={!hasApprovalLimit}
                            >
                              Approve
                            </MenuItem>
                            <MenuItem
                              onClick={() => {
                                setApproversOpen(true);
                                handleMenuClose();
                              }}
                            >
                              Approval Info
                            </MenuItem>
                          </Menu>
                        </>
                      );
                    })()}
                  </Box>
                </>
              );
            })()}
          </Box>
          {/* Right: Buttons */}
          <Box
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "12px",
            }}
          >
            {tenantId !== '3ce307df-cf59-444d-b9ce-6c713b4170e8' && (
              <Button
                onClick={handleRefresh}
                disabled={isRefreshing || isUnderwriting || isUnderwritingActive(currentCompany)}
                style={{
                  color: "#5186EC",
                  textTransform: "none",
                  fontFamily: "Rubik, sans-serif",
                  minWidth: "50px",
                  height: "36px",
                  fontWeight: "250",
                  whiteSpace: "nowrap",
                  opacity: (isRefreshing || isUnderwriting || isUnderwritingActive(currentCompany)) ? 0.5 : 1,
                }}
                startIcon={
                  <RefreshIcon
                    style={{
                      color: "#5186EC",
                      animation: (isRefreshing || isUnderwriting || isUnderwritingActive(currentCompany)) ? "spin 1s linear infinite" : "none"
                    }}
                  />
                }
              >
                Refresh
              </Button>
            )}
            {financialScopeTenantId && showProviderFinancials && (
              <Tooltip title={financialReportDisabledReason || 'Get financial statements'}>
                <span>
                  <Button
                    variant="outlined"
                    onClick={handleGetFinancialStatements}
                    disabled={isImportingDnb || Boolean(financialReportDisabledReason)}
                    startIcon={isImportingDnb ? <CircularProgress size={16} /> : <CloudDownloadOutlinedIcon />}
                    sx={{ color: '#5186EC', borderColor: '#5186EC', textTransform: 'none', height: 36, whiteSpace: 'nowrap', borderRadius: '7px' }}
                  >
                    {isImportingDnb ? 'Getting financials…' : 'Get financials'}
                  </Button>
                </span>
              </Tooltip>
            )}
            {isAdmin && (
              <AssessmentReview
                tenantName={tenantName}
                entityName={currentCompany?.name || currentCompany?.companyName}
                entity={assessmentEntity}
              />
            )}
            <Button
              onClick={handleActionsClick}
              style={{
                color: "#5186EC",
                textTransform: "none",
                fontFamily: "Rubik, sans-serif",
                height: "36px",
                fontWeight: "250",
                whiteSpace: "nowrap",
              }}
              startIcon={<TuneIcon style={{ color: "#5186EC" }} />}
              endIcon={<ArrowDropDownIcon style={{ color: "#5186EC" }} />}
            >
              Actions
            </Button>
            <Menu
              anchorEl={anchorElActions}
              open={openActions}
              onClose={handleActionsClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              {permissions.canVerifyEntity && (
                <Box onClick={handleActionsClose} sx={{ px: 2, py: 0.75 }}>
                  <VerifyEntityModal
                    handleDelete={() => {}}
                    companyUniqueID={currentCompany?.dnbCompanyId}
                    companyInfo={currentCompany}
                    requestedLimit={currentCompany?.requestedCreditLimit}
                  />
                </Box>
              )}
              <MenuItem
                onClick={() => { setShowUserNotes(true); handleActionsClose(); }}
                sx={{ fontFamily: "Rubik, sans-serif", fontSize: 14, color: "#2F3D63" }}
              >
                <NoteAddIcon style={{ color: "#5186EC", marginRight: 10, fontSize: 20 }} /> User Notes
              </MenuItem>
              {permissions.canDeleteEntity && (
                <MenuItem
                  disabled={isDeleting}
                  onClick={() => { handleDeleteCompany(); handleActionsClose(); }}
                  sx={{ fontFamily: "Rubik, sans-serif", fontSize: 14, color: "#2F3D63" }}
                >
                  <DeleteIcon style={{ color: "#5186EC", marginRight: 10, fontSize: 20 }} /> Delete Company
                </MenuItem>
              )}
              <MenuItem
                disabled={isExporting}
                onClick={() => { handleExport(); handleActionsClose(); }}
                sx={{ fontFamily: "Rubik, sans-serif", fontSize: 14, color: "#2F3D63" }}
              >
                <FileDownloadIcon style={{ color: "#5186EC", marginRight: 10, fontSize: 20 }} /> {isExporting ? "Exporting…" : "Export"}
              </MenuItem>
            </Menu>

          </Box>
        </Box>
      </div>

      {canRetryFinancialAccess && (
        <Alert
          severity="info"
          role="status"
          sx={{ mx: 3, mb: 2 }}
          action={
            <Button
              color="inherit"
              onClick={() => setFinancialAccessRevision((value) => value + 1)}
              disabled={isImportingDnb}
              sx={{ textTransform: 'none' }}
            >
              Check financials access again
            </Button>
          }
        >
          {financialReportDisabledReason}
        </Alert>
      )}
      
      <Paper
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          backgroundColor: "#FAFAFC",
        }}>
        <div>
          <Tabs
            sx={{
              borderBottom: 1,
              paddingTop: "20px",
              paddingLeft: "25px",
              "& .MuiTabs-indicator": {
                backgroundColor: "#2F3D63",
                borderRadius: "8px 8px 0px 0px",
                height: "3px",
              },
              "& .MuiTab-root": {
                color: "#777A82",
                textTransform: "none",
                height: "24px",
                fontSize: "20px",
                fontFamily: "Rubik , sans-serif",
                minWidth: "140px",
                fontWeight: "500",
              },
              "& .Mui-selected": {
                color: "#2F3D61",
                fontWeight: "700",
              },
            }}
            value={selectedTab}
            onChange={handleTabChange}
            aria-label='company tabs'>
            <Tab label='General Info' {...a11yProps(0)} />
            <Tab label='Files' {...a11yProps(1)} />
            <Tab label='Questions' {...a11yProps(2)} />
            <Tab label='Additional Info' {...a11yProps(3)} />
            <Tab label='Logs' {...a11yProps(4)} />
            <Tab label='Financial Report' {...a11yProps(5)} />
          </Tabs>
          
          <TabPanel value={selectedTab} index={0}>
              {/* Executive Summary Section */}
              {(currentCompany?.summaryData) && (
                <>
                  <Typography
                    style={{
                      textAlign: "left",
                      fontSize: "20px",
                      color: "#1A2A56",
                      fontWeight: "600",
                      width: "290px",
                      height: "24px",
                      padding: " 0 25px",
                      marginTop: "20px",
                    }}>
                    Executive Summary
                  </Typography>
                  <ExecutiveSummary
                    company={currentCompany}
                    resolvedData={resolvedData}
                    summary={currentCompany?.summaryData}
                    overview={companyOverviewData}
                    incomeStatement={companyIncomeStatementData}
                    AgingDataForCompany={agingData}
                    DnbData={DnbData}
                    tenantId={tenantId}
                    linkedInJSON={linkedInJSON}
                    setShowExport={setShowExport}
                    setCurrentCompany={setCurrentCompany}
                    dnBFinancialV2Data={dnBFinancialV2Data}
                    dnbJsonData={dnbJsonData}
                    executiveData={executiveData}
                    summaryScores={summaryScores}
                    userInfo={userInfo}
                    reFetchCompanyData={reFetchSummaryData}
                    showInsured={isInsuranceEnabled}
                    insuredLimit={insuredLimit}
                  />
                  <PathToRequestedLimitCard
                    tenantId={tenantId}
                    company={currentCompany}
                  />
                </>
              )}

              {/* Underwriting Copilot lives ONLY inside the AI Assessment Review (admin) — not on the report page. */}

              {/* DNB Control Status Section */}
              {currentCompany?.dunsControlStatus && (
                <DunsControlStatus dunsControlStatus={currentCompany?.dunsControlStatus} tenantId={tenantId} />
              )}

              <Box sx={{ mx: 3, mt: 3, p: 2.5, border: '1px solid #D9E5F7', borderRadius: 2.5, bgcolor: '#fff', boxShadow: '0 2px 8px rgba(25, 42, 86, 0.04)' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2.25 }}>
                  <Box>
                    <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#1A2A56' }}>External Ratings</Typography>
                    <Typography sx={{ mt: 0.4, fontSize: 13, color: '#64748B' }}>Current long-term issuer ratings from major credit-rating agencies.</Typography>
                  </Box>
                  {Array.isArray(currentCompany?.externalRatings) && currentCompany.externalRatings.length > 0 && (
                    <Chip size="small" icon={<VerifiedOutlinedIcon sx={{ fontSize: 15 }} />} label={`${currentCompany.externalRatings.length} ratings found`} sx={{ bgcolor: '#EEF6FF', color: '#1E5FAF', fontWeight: 700, border: '1px solid #CFE2FF' }} />
                  )}
                </Box>
                {Array.isArray(currentCompany?.externalRatings) && currentCompany.externalRatings.length > 0 ? (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
                    {currentCompany.externalRatings.map((rating, index) => {
                      const agency = rating?.Agency || rating?.agency || 'Rating agency';
                      const grade = rating?.Long_Term_Rating || rating?.longTermRating || rating?.rating || 'Not rated';
                      const outlook = rating?.Outlook || rating?.outlook || 'Not stated';
                      const updated = rating?.Last_Update || rating?.lastUpdate || rating?.Date || rating?.date;
                      const sourceUrl = rating?.source_url || rating?.sourceUrl;
                      return (
                        <Box key={`${agency}-${index}`} sx={{ minWidth: 0, p: 1.75, border: '1px solid #E5EDF8', borderRadius: 2, bgcolor: '#FBFDFF', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                            <Typography sx={{ minWidth: 0, fontSize: 14, fontWeight: 700, color: '#243B67' }}>{agency}</Typography>
                            <Chip label={grade} size="small" sx={{ height: 26, bgcolor: '#FFF5DE', color: '#9A6500', border: '1px solid #F7D78D', fontWeight: 800, fontSize: 13 }} />
                          </Box>
                          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                            <Box>
                              <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.45, textTransform: 'uppercase', color: '#8090A8' }}>Outlook</Typography>
                              <Typography sx={{ mt: 0.2, fontSize: 13.5, fontWeight: 600, color: '#3C4F70' }}>{outlook}</Typography>
                            </Box>
                            <Box>
                              <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.45, textTransform: 'uppercase', color: '#8090A8' }}>As of</Typography>
                              <Typography sx={{ mt: 0.2, fontSize: 13.5, fontWeight: 600, color: '#3C4F70' }}>{updated || 'Not stated'}</Typography>
                            </Box>
                          </Box>
                          {sourceUrl && (
                            <Box component="a" href={sourceUrl} target="_blank" rel="noreferrer" sx={{ mt: 0.25, display: 'inline-flex', alignItems: 'center', gap: 0.5, width: 'fit-content', color: '#246BCE', fontSize: 12.5, fontWeight: 700, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                              View rating action <OpenInNewIcon sx={{ fontSize: 14 }} />
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Typography sx={{ color: '#64748B' }}>
                    No external credit ratings were found for this company yet. Refresh underwriting to run a new ratings search.
                  </Typography>
                )}
              </Box>

              <CompanyNewsResearch research={currentCompany?.companyNewsResearch} />

              {/* Company Summary Section — saved result; generation is explicit or part of refresh. */}
              {currentCompany?.aiSummaryText ? (
                <CompanySummary
                  company={currentCompany}
                  setCompanySummary={setCompanySummary}
                />
              ) : aiSummaryGenerating ? (
                <Box sx={{ p: 4, textAlign: "center", color: "#64748b", display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
                  <CircularProgress size={22} />
                  <Typography sx={{ fontSize: 14 }}>Generating company summary with AI…</Typography>
                </Box>
              ) : aiSummaryFailed ? (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Typography sx={{ fontSize: 14, color: "#64748b", mb: 0.75 }}>Company summary isn’t available.</Typography>
                  {aiSummaryError && (
                    <Typography sx={{ fontSize: 12.5, color: "#b45309", mb: 1.5 }}>{aiSummaryError}</Typography>
                  )}
                  <Button variant="outlined" size="small" onClick={generateSummaryNow} sx={{ textTransform: "none" }}>Generate with AI</Button>
                </Box>
              ) : (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Typography sx={{ fontSize: 14, color: "#64748b", mb: 1.5 }}>Company summary isn’t available yet.</Typography>
                  <Button variant="outlined" size="small" onClick={generateSummaryNow} sx={{ textTransform: "none" }}>Generate with AI</Button>
                </Box>
              )}

              {/* Financial Info Section */}
              <CompanyFinancialInfo
                showProviderFinancials={showProviderFinancials}
                financialData={!permissionsLoading && permissions.canViewFinancialData === true ? financialData : null}
                onGetFinancials={handleGetFinancialStatements}
                financialsDisabledReason={financialReportDisabledReason}
                isGettingFinancials={isImportingDnb}
              />

              {/* Company Tree Section */}
              {currentCompany?.companyTreeData && (
                <CompanyTree
                  treeData={currentCompany?.companyTreeData}
                  tenantId={tenantId}
                  isAdmin={isAdmin}
                />
              )}

              {/* User Notes Section */}
              {(currentCompany?.userNotes || showUserNotes) && (
                <div ref={userNotesRef}>
                  <UserNotes
                    id="user-notes-section"
                    company={currentCompany}
                    tenantId={tenantId}
                    userInfo={userInfo}
                    onNotesUpdate={(notes) => {
                      // Update company object with new notes
                      setCurrentCompany({ ...currentCompany, userNotes: notes });
                      if (!notes || notes.length === 0) {
                        setShowUserNotes(false);
                      }
                    }}
                  />
                </div>
              )}

              {/* Alternative Data Section */}
              {alternativeData && (
                <>
                  <Box
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "0 25px",
                      marginTop: "20px",
                      marginBottom: "20px"
                    }}>
                    <Typography
                      style={{
                        textAlign: "left",
                        fontSize: "20px",
                        color: "#1A2A56",
                        fontWeight: "600",
                        width: "290px",
                        height: "24px",
                      }}>
                      Alternative Data
                    </Typography>
                    {isAdmin && (
                      <Button
                        onClick={refreshAlternativeData}
                        disabled={isRefreshingAlternativeData}
                        style={{
                          color: "#5186EC",
                          textTransform: "none",
                          fontFamily: "Rubik, sans-serif",
                          width: "56px",
                          height: "17px",
                          fontWeight: "250",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px"
                        }}
                        startIcon={
                          <RefreshIcon 
                            style={{ 
                              color: "#5186EC",
                              animation: isRefreshingAlternativeData ? "spin 1s linear infinite" : "none"
                            }} 
                          />
                        }>
                        Refresh
                      </Button>
                    )}
                  </Box>
                  <Box
                    style={{
                      background: "#FAFAFC 0% 0% no-repeat padding-box",
                      boxShadow: "inset 0px 1px 3px #0000001A, 0px 3px 6px #00000029",
                      borderRadius: "8px",
                      opacity: 1,
                      padding: "25px",
                      margin: "0 25px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "15px",
                    }}>
                    <div
                      style={{
                        color: "#4A4A4A",
                        lineHeight: "1.6",
                        fontSize: "16px",
                      }}>
                      {typeof window !== 'undefined' &&
                        window?.localStorage?.getItem('debugReportViewer') === '1' &&
                        process?.env?.NODE_ENV !== 'production' && (
                          (() => {
                            try {
                              // eslint-disable-next-line no-console
                              console.groupCollapsed('[CompanyPage debug] alternativeData.report');
                              // eslint-disable-next-line no-console
                              console.log('tenantId:', tenantId);
                              // eslint-disable-next-line no-console
                              console.log('alternativeData.report:', alternativeData?.report);
                              // eslint-disable-next-line no-console
                              console.groupEnd();
                            } catch (_) {}
                            return null;
                          })()
                        )}
                      <ReportViewer markdownText={alternativeData.report} tenantId={tenantId} />
                    </div>
                  </Box>
                </>
              )}

              {/* News Section */}
              <SimpleNews newsData={newsData || []} tenantId={tenantId} companyId={effectiveCompanyId} />

              {/* Company Event Timeline Section */}
              {timelineData && timelineData.length > 0 && <CompanyEventTimeline timelineData={timelineData} />}

          </TabPanel>
          <TabPanel value={selectedTab} index={1}>
            <ChatFile company={currentCompany} tenantId={tenantId} financialData={financialData}/>
          </TabPanel>
          <TabPanel value={selectedTab} index={2}>
            <ContextOverview contextQA={currentCompany?.contextQA} />
            <QuestionsAccordion
              questions={questions}
              setQuestions={setQuestions}
              companyId={effectiveCompanyId}
              tenantId={tenantId}
            />
          </TabPanel>
          <TabPanel value={selectedTab} index={3}>
            <Box
              style={{
                background: "#FAFAFC 0% 0% no-repeat padding-box",
                boxShadow: "inset 0px 1px 3px #0000001A, 0px 3px 6px #00000029",
                borderRadius: "8px",
                opacity: 1,
                padding: "25px",
                display: "flex",
                flexDirection: "column",
                gap: "15px",
              }}>
              <Typography
                style={{
                  color: "#2F3D63",
                  fontSize: "20px",
                  textAlign: "left",
                  fontWeight: "500",
                }}>
                Additional Info
              </Typography>
              <div
                style={{
                  color: "#4A4A4A",
                  lineHeight: "1.6",
                  fontSize: "16px",
                }}>
                <div dangerouslySetInnerHTML={{ __html: additionalInfo }} />
              </div>
            </Box>
          </TabPanel>
          <TabPanel value={selectedTab} index={4}>
            <Logs tenantId={tenantId} companyId={effectiveCompanyId} companyName={currentCompany?.name || currentCompany?.companyName} />
          </TabPanel>
          <TabPanel value={selectedTab} index={5}>
            <FinancialReport
              showProviderFinancials={showProviderFinancials}
              tenantId={tenantId}
              effectiveCompanyId={effectiveCompanyId}
              refreshKey={financialReportRefreshKey}
              onGetFinancials={handleGetFinancialStatements}
              financialsDisabledReason={financialReportDisabledReason}
              isGettingFinancials={isImportingDnb}
              canViewFinancialData={!permissionsLoading && permissions.canViewFinancialData === true}
              canDownloadReports={!permissionsLoading && permissions.canDownloadReports === true}
            />
          </TabPanel>
              </div>
      </Paper>

      <Dialog open={dnbImportOpen} onClose={() => !isImportingDnb && setDnbImportOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ color: '#1A2A56', fontWeight: 700, pr: 6 }}>
          Get financials
          <IconButton aria-label="Close financial statements dialog" onClick={() => setDnbImportOpen(false)} disabled={isImportingDnb} sx={{ position: 'absolute', right: 12, top: 10 }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {isImportingDnb && <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 3 }}><CircularProgress size={28} aria-label="Retrieving financial statements" /><Box><Typography sx={{ fontWeight: 700 }}>Retrieving financial statements…</Typography><Typography variant="body2" color="text.secondary">The result will be added to the Financial Report and used to refresh the alternative-data assessment.</Typography></Box></Box>}
          {dnbImportError && <Alert severity="error">{dnbImportError}</Alert>}
          {dnbImportResult && <Box>
            <Alert severity={dnbImportResult.availability === 'unavailable' || dnbImportResult.alternativeAssessment?.success === false ? 'warning' : 'success'} sx={{ mb: 2 }}>
              {dnbImportResult.availability === 'unavailable'
                ? (dnbImportResult.message || 'No verified financial statements are available for this company.')
                : 'Financials retrieved and added to the Financial Report tab.'}
              {dnbImportResult.availability !== 'unavailable' && dnbImportResult.alternativeAssessment?.success
                ? ' The alternative-data assessment was refreshed using these statements.'
                : dnbImportResult.alternativeAssessment?.success === false
                  ? ' The alternative-data assessment was not refreshed; retry it from the report.'
                  : ''}
            </Alert>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 2 }}><Box><Typography variant="caption" color="text.secondary">MATCHED COMPANY</Typography><Typography sx={{ fontWeight: 700 }}>{currentCompany?.name || currentCompany?.companyName}</Typography>{dnbImportResult.source?.dunsNumber && <Typography variant="body2" color="text.secondary">Reference ID {dnbImportResult.source.dunsNumber}</Typography>}</Box><Chip label="Verified source" color="success" variant="outlined" /></Box>
            {dnbImportResult.availability !== 'unavailable' && <Typography variant="body2">{dnbImportResult.statements?.length || 0} statement periods added · Data status: {dnbImportResult.source?.blockStatus || 'received'}</Typography>}
          </Box>}
          {dnbDiagnostics && <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography sx={{ color: '#1A2A56', fontWeight: 700, mb: 1 }}>D&amp;B API request and response</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Authentication credentials are redacted. The response below is the body returned for this attempt.
            </Typography>
            {(Array.isArray(dnbDiagnostics) ? dnbDiagnostics : [dnbDiagnostics]).map((exchange, index) => <Box key={`${exchange?.operation || 'request'}-${index}`} sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.75 }}>{exchange?.operation || `Request ${index + 1}`}</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>REQUEST</Typography>
                  <Box component="pre" sx={{ m: 0, mt: 0.5, p: 1.5, bgcolor: '#F6F8FC', border: '1px solid #DDE3EE', borderRadius: 1, fontSize: 12, lineHeight: 1.45, maxHeight: 300, overflow: 'auto', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                    {formatDiagnosticJson(exchange?.request)}
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>RESPONSE</Typography>
                  <Box component="pre" sx={{ m: 0, mt: 0.5, p: 1.5, bgcolor: '#F6F8FC', border: '1px solid #DDE3EE', borderRadius: 1, fontSize: 12, lineHeight: 1.45, maxHeight: 300, overflow: 'auto', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                    {formatDiagnosticJson(exchange?.response)}
                  </Box>
                </Box>
              </Box>
            </Box>)}
          </Box>}
        </DialogContent>
        <DialogActions><Button onClick={() => setDnbImportOpen(false)} disabled={isImportingDnb}>Close</Button>{dnbImportError && <Button variant="contained" onClick={handleGetFinancialStatements} disabled={isImportingDnb || Boolean(financialReportDisabledReason)}>Try again</Button>}{dnbImportResult?.availability !== 'unavailable' && dnbImportResult && <Button variant="contained" onClick={() => { setSelectedTab(5); setDnbImportOpen(false); }}>View financial report</Button>}</DialogActions>
      </Dialog>

      {/* Approve confirmation dialog */}
        {Array.isArray(currentCompany?.approvers) && currentCompany.approvers.length > 0 && (
        <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)}>
          <DialogTitle>Confirm Approval</DialogTitle>
          <DialogContent>
            Are you sure you want to approve this company?
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleApprove} color="primary">Approve</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Approvers list card/modal */}
      {Array.isArray(currentCompany?.approvers) && currentCompany.approvers.length > 0 && (
        <Dialog 
          open={approversOpen} 
          onClose={() => setApproversOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            style: {
              borderRadius: 16,
              minWidth: 480,
              maxWidth: 600,
              minHeight: 400,
              padding: 0,
            }
          }}
        >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1, pr: 2 }}>
          <span style={{ fontWeight: 700, fontSize: 22, color: '#1A2A56' }}>Approvers</span>
          <IconButton onClick={() => setApproversOpen(false)} size="small" aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 3, pt: 2, maxHeight: 400, overflowY: 'auto', bgcolor: '#F7F8FA' }}>
          {currentCompany?.approvers?.length === 0 && (
            <Typography sx={{ color: '#888', textAlign: 'center', mt: 4 }}>No approvers found.</Typography>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {currentCompany?.approvers?.map((approver, idx) => {
              // Only show send email button if the logged-in user is an approver
              const isSpecialRole = ['YRNwGPczYAS9GMMa9ary', 'jHiUDvpJjSuv8cIdpOc6'].includes(userInfo?.roleId);
              const canSendEmail =
                permissions.canApprove &&
                approver?.userId !== subId &&
                !approver?.approvedAt &&
                (
                  isSpecialRole || currentCompany?.approvers?.some(a => a.userId === subId && !a.approvedAt)
                );
              return (
                <Paper
                  key={idx}
                  elevation={2}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    p: 2,
                    borderRadius: 3,
                    bgcolor: "#fff",
                    gap: 2,
                    position: "relative",
                    minHeight: 80, // Ensure minimum height for proper positioning
                  }}
                >
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      fontWeight: 600,
                      bgcolor: "#E6F4EA",
                      color: "#219653",
                      fontSize: 22,
                    }}
                  >
                    {approver.name?.[0] || "?"}
                  </Avatar>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: 18,
                        color: "#1A2A56",
                        mb: 0.5,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {approver.name}
                    </Typography>

                    <Typography sx={{ color: "#777", fontSize: 15, mb: 0.5 }}>
                      <b>Role:</b> {approver.roleName}
                    </Typography>

                    <Chip
                      label={approver.approvedAt ? "Approved" : "Pending"}
                      color={approver.approvedAt ? "success" : "default"}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        fontSize: 16,
                        mr: 1,
                        mb: 0.5,
                        px: 2,
                        height: 32,
                        ...(approver.approvedAt
                          ? {}
                          : {
                              bgcolor: "#FFF3CD", // light yellow
                              color: "#B8860B", // dark gold
                              border: "1px solid #FFE082",
                            }),
                      }}
                    />
                    {approver.approvedAt && (
                      <Typography sx={{ color: "#219653", fontSize: 13, mt: 0.5 }}>
                        {(() => {
                          const dateObj = parseFirestoreTimestamp(approver.approvedAt);
                          return dateObj
                            ? `Approved at ${dateObj.toLocaleString()}`
                            : "Approved (date unavailable)";
                        })()}
                      </Typography>
                    )}
                  </Box>

                  {/* Right-side actions */}
                  <Box sx={{ 
                    display: "flex", 
                    flexDirection: "column", 
                    alignItems: "flex-end", 
                    ml: "auto",
                    height: "100%",
                    justifyContent: "center", // This will push content to top and bottom
                    minHeight: 90, // Minimum height to ensure proper spacing
                  }}>
                    {canSendEmail && (
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        sx={{ fontWeight: 600, borderRadius: 2 }}
                        onClick={() => handleSendApprovalRequestEmail(approver)}
                      >
                        Send Email
                      </Button>
                    )
                    }

                    {approver.lastEmailSentAt && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ 
                          fontSize: 11,
                          textAlign: 'right',
                          lineHeight: 1.2,
                          mt: 1  
                        }}
                      >
                        Last Email sent at{" "}
                        {new Date(approver.lastEmailSentAt).toLocaleString("en-GB", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: false,
                        })}
                      </Typography>
                    )}
                  </Box>
                </Paper>

              );
            })}
          </Box>
      </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button onClick={() => setApproversOpen(false)} variant="outlined" color="primary" sx={{ borderRadius: 2, fontWeight: 600, minWidth: 100 }}>Close</Button>
        </DialogActions>
      </Dialog>
      )}
    </div>
  );
}

export default CompanyPage;
